import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, Menu, X, LogOut, User } from 'lucide-react';
import SafeImage from './SafeImage';
import { useAuth } from '../context/AuthContext';

const NavItem = ({ to, label, active, accent = 'default', locked = false }) => (
  <Link
    to={to}
    className={`relative px-3 py-2 text-sm font-semibold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5 ${
      active
        ? (accent === 'gold' ? 'text-yellow-300' : 'text-orange-300')
        : (accent === 'gold' ? 'text-zinc-300 hover:text-yellow-300 hover:[text-shadow:0_0_10px_rgba(250,204,21,0.35)]' : 'text-zinc-300 hover:text-cyan-200')
    }`}
  >
    {label}
    {locked && <Lock className="w-3 h-3 text-yellow-400/80" />}
    <span
      className={`absolute left-2 right-2 -bottom-0.5 h-[2px] transition-opacity ${
        active ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ background: accent === 'gold' ? 'linear-gradient(90deg,#ca8a04,#fde047,#ca8a04)' : 'linear-gradient(90deg,#ff6b1a,#00e5ff)' }}
    />
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthed, user, logout } = useAuth();
  const canViewHallOfFame = ['immortal', 'radiant'].includes((user?.plan || 'free').toLowerCase());

  const onLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed inset-x-0 z-[100] bg-[#05070b]/85 backdrop-blur-md border-b border-orange-500/10 transition-[top] duration-200" style={{ top: 'var(--layout-ticker-offset, 0px)' }}>
      <div className="glitch-line top-0" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative w-9 h-9 flex items-center justify-center rounded-sm border border-orange-500/60 bg-orange-500/10 group-hover:border-cyan-300/70 transition-colors overflow-hidden">
              <SafeImage src="/valorex-logo.png" alt="ValoRex" className="w-8 h-8 object-contain" fallbackClassName="w-8 h-8" />
            </div>
            <div className="font-mono-display text-lg sm:text-xl font-bold">
              <span className="neon-orange">VALO</span><span className="white">REX</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/" label="Home" active={pathname === '/'} />
            <NavItem to="/pricing" label="Pricing" active={pathname === '/pricing'} />
            {isAuthed && <NavItem to="/hall-of-fame" label="Hall of Fame" active={pathname === '/hall-of-fame'} accent="gold" locked={!canViewHallOfFame} />}
            {isAuthed && <NavItem to="/dashboard" label="Dashboard" active={pathname.startsWith('/dashboard')} />}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {!isAuthed ? (
              <>
                <Link to="/auth/login" className="btn-ghost">Sign In</Link>
                <Link to="/auth/register" className="btn-neon">Get Started</Link>
              </>
            ) : (
              <>
                <Link to="/dashboard" className="flex items-center gap-2 px-3 py-1.5 rounded border border-cyan-400/30 bg-cyan-400/5 hover:border-cyan-300/60 transition-colors">
                  <User className="w-4 h-4 text-cyan-300" />
                  <span className="text-sm text-cyan-100">{user?.username}</span>
                </Link>
                <button onClick={onLogout} className="btn-ghost"><LogOut className="w-3.5 h-3.5"/>Logout</button>
              </>
            )}
          </div>

          <button className="md:hidden text-zinc-200" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 space-y-2 border-t border-orange-500/20 pt-3">
            <Link to="/" className="block py-2 text-zinc-200" onClick={()=>setOpen(false)}>Home</Link>
            <Link to="/pricing" className="block py-2 text-zinc-200" onClick={()=>setOpen(false)}>Pricing</Link>
            {isAuthed && (
              <Link to="/hall-of-fame" className="flex items-center gap-2 py-2 text-zinc-200 hover:text-yellow-300" onClick={()=>setOpen(false)}>
                Hall of Fame
                {!canViewHallOfFame && <Lock className="w-3 h-3 text-yellow-400/80" />}
              </Link>
            )}
            {isAuthed && <Link to="/dashboard" className="block py-2 text-zinc-200" onClick={()=>setOpen(false)}>Dashboard</Link>}
            {!isAuthed ? (
              <div className="flex gap-2 pt-2">
                <Link to="/auth/login" className="btn-ghost flex-1" onClick={()=>setOpen(false)}>Sign In</Link>
                <Link to="/auth/register" className="btn-neon flex-1" onClick={()=>setOpen(false)}>Get Started</Link>
              </div>
            ) : (
              <button onClick={onLogout} className="btn-ghost w-full">Logout</button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
